import { useEffect, useState } from "react";
import { Car, AlertTriangle, CheckCircle, XCircle, Crown, Star, Sparkles, Users, DollarSign, TrendingUp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const TIER_OPTIONS = [
  { value: "basic", label: "Basic", icon: Star },
  { value: "medium", label: "Medium", icon: Sparkles },
  { value: "elite", label: "Elite", icon: Crown },
];

export default function AdminChauffeurTab() {
  const [section, setSection] = useState("rides"); // rides | drivers | tiers | sos | revenue
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [users, setUsers] = useState([]);
  const [sosFeed, setSosFeed] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [settings, setSettings] = useState({ commission_percent: 7 });
  const [pctDraft, setPctDraft] = useState("5");
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, d, u, s, rev, set_] = await Promise.all([
        api.get("/admin/chauffeur/rides"),
        api.get("/admin/chauffeur/drivers"),
        api.get("/admin/users"),
        api.get("/admin/chauffeur/sos-feed"),
        api.get("/admin/chauffeur/revenue?days=30").catch(() => ({ data: null })),
        api.get("/chauffeur/platform-settings").catch(() => ({ data: { commission_percent: 7 } })),
      ]);
      setRides(r.data || []);
      setDrivers(d.data || []);
      setUsers(u.data || []);
      setSosFeed(s.data || []);
      setRevenue(rev.data);
      setSettings(set_.data || { commission_percent: 7 });
      setPctDraft(String(set_.data?.commission_percent ?? 7));
    } catch (err) {
      toast.error("Failed to load chauffeur data");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); const t = setInterval(loadAll, 12000); return () => clearInterval(t); }, []);

  const setApproval = async (driverId, status) => {
    try {
      await api.patch(`/admin/chauffeur/drivers/${driverId}/approval`, { status });
      toast.success(`Driver ${status}`);
      loadAll();
    } catch (err) { toast.error(errorMessage(err, "Update failed")); }
  };

  const setUserTier = async (email, tier) => {
    try {
      await api.patch(`/admin/users/${encodeURIComponent(email)}/tier`, { tier });
      toast.success(`${email} → ${tier}`);
      loadAll();
    } catch (err) { toast.error(errorMessage(err, "Tier update failed")); }
  };

  const saveCommission = async () => {
    const pct = Number(pctDraft);
    if (Number.isNaN(pct) || pct < 0 || pct > 50) { toast.error("Enter a value between 0 and 50"); return; }
    try {
      const res = await api.patch("/admin/chauffeur/platform-settings", { commission_percent: pct });
      setSettings(res.data);
      toast.success(`Platform fee set to ${res.data.commission_percent}%`);
    } catch (err) { toast.error(errorMessage(err, "Save failed")); }
  };

  const sectionBtn = (id, label) => (
    <button data-testid={`chauffeur-section-${id}`} onClick={() => setSection(id)}
      className={`px-4 py-2 text-xs font-body uppercase tracking-wider transition-colors border ${
        section === id ? "border-gold/40 text-gold bg-gold/10" : "border-white/10 text-white/50 hover:text-white"
      }`}>{label}</button>
  );

  return (
    <div data-testid="chauffeur-tab" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/60">Chauffeur Operations</p>
          <h3 className="text-white font-heading text-xl">Live Rides &amp; Driver Management</h3>
        </div>
        <div className="flex gap-2 flex-wrap">
          {sectionBtn("rides", `Rides (${rides.length})`)}
          {sectionBtn("drivers", `Drivers (${drivers.length})`)}
          {sectionBtn("tiers", `User Tiers (${users.length})`)}
          {sectionBtn("revenue", "Revenue")}
          {sectionBtn("sos", `SOS Feed (${sosFeed.length})`)}
        </div>
      </div>

      {section === "revenue" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div data-testid="revenue-volume" className="p-4 border border-gold/30 bg-gold/5 rounded-md">
              <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/80 mb-1 flex items-center gap-1"><DollarSign size={10} /> 30-day Volume</p>
              <p className="font-heading text-3xl text-white">JMD {revenue?.total_volume_jmd?.toLocaleString() || 0}</p>
              <p className="text-white/40 text-xs font-body mt-1">{revenue?.completed_rides || 0} completed rides</p>
            </div>
            <div data-testid="revenue-fees" className="p-4 border border-gold/30 bg-gold/5 rounded-md">
              <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/80 mb-1 flex items-center gap-1"><TrendingUp size={10} /> 30-day Platform Fees</p>
              <p className="font-heading text-3xl text-gold">JMD {revenue?.total_platform_fees?.toLocaleString() || 0}</p>
              <p className="text-white/40 text-xs font-body mt-1">Taxi: JMD {revenue?.fees_by_type?.taxi || 0} · Delivery: JMD {revenue?.fees_by_type?.food || 0}</p>
            </div>
            <div className="p-4 border border-white/10 bg-white/5 rounded-md">
              <p className="text-[10px] font-body uppercase tracking-[0.2em] text-white/60 mb-1">Driver Payouts</p>
              <p className="font-heading text-3xl text-white">JMD {revenue?.total_driver_payouts?.toLocaleString() || 0}</p>
              <p className="text-white/40 text-xs font-body mt-1">Cash on completion (Stripe Connect coming soon)</p>
            </div>
          </div>
          <div className="p-4 border border-white/10 bg-white/5 rounded-md">
            <p className="text-[10px] font-body uppercase tracking-[0.2em] text-white/60 mb-2">Platform Commission</p>
            <div className="flex items-center gap-2 max-w-sm">
              <Input data-testid="commission-input" type="number" min={0} max={50} step={0.5}
                value={pctDraft} onChange={(e) => setPctDraft(e.target.value)}
                className="bg-luxury-black/60 border-gold/30 text-gold font-heading text-xl" />
              <span className="text-gold font-body text-sm">%</span>
              <button data-testid="commission-save" onClick={saveCommission}
                className="bg-gold text-luxury-black px-4 py-2 text-xs font-body uppercase tracking-wider hover:bg-gold/90 transition-colors">
                Save
              </button>
            </div>
            <p className="text-white/40 text-xs font-body mt-2">Currently {settings.commission_percent}% applied to every completed ride. Drivers see this on their dashboard.</p>
          </div>
        </div>
      )}

      {section === "sos" && (
        <div className="space-y-3">
          {sosFeed.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-md p-12 text-center text-white/40 font-body text-sm">No SOS alerts</div>
          ) : sosFeed.map((r) => (
            <div key={r.id} data-testid={`sos-card-${r.id}`} className="p-4 border border-red-500/40 bg-red-500/5 rounded-md flex items-start gap-3">
              <AlertTriangle className="text-red-400 mt-0.5" size={18} />
              <div className="flex-1">
                <p className="text-white text-sm font-body"><span className="text-red-300">SOS</span> · {r.client_name} · ride {r.id?.slice(0, 8)}</p>
                <p className="text-white/60 text-xs font-body">{r.pickup?.address} → {r.dropoff?.address}</p>
                <p className="text-white/40 text-xs font-body mt-1">Last: {r.sos_last_at}</p>
              </div>
              <span className="text-xs font-body px-2 py-1 bg-white/5 text-white/60">{r.status}</span>
            </div>
          ))}
        </div>
      )}

      {section === "drivers" && (
        <div className="space-y-3">
          {drivers.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-md p-12 text-center text-white/40 font-body text-sm">No drivers yet</div>
          ) : drivers.map((d) => (
            <div key={d.id} data-testid={`driver-card-${d.id}`} className="p-4 border border-white/10 bg-white/5 rounded-md flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Car className={d.online_status === "online" ? "text-green-400" : "text-white/40"} size={18} />
                <div className="min-w-0">
                  <p className="text-white text-sm font-body truncate">{d.user_name} · <span className="text-gold">{d.license_plate}</span></p>
                  <p className="text-white/50 text-xs font-body truncate">{d.vehicle_color} {d.vehicle_make} {d.vehicle_model} · {d.user_email}</p>
                  <p className="text-white/40 text-[10px] font-body">⭐ {d.rating?.toFixed(1) || "5.0"} · {d.total_rides} rides</p>
                </div>
              </div>
              <span className={`px-2 py-1 text-[10px] font-body uppercase tracking-wider ${
                d.approval_status === "approved" ? "text-green-400 bg-green-400/10" :
                d.approval_status === "rejected" ? "text-red-400 bg-red-400/10" : "text-amber-300 bg-amber-300/10"
              }`}>{d.approval_status}</span>
              {d.approval_status !== "approved" && (
                <button data-testid={`approve-${d.id}`} onClick={() => setApproval(d.id, "approved")}
                  className="text-green-400 hover:text-green-300 transition-colors" title="Approve">
                  <CheckCircle size={18} />
                </button>
              )}
              {d.approval_status !== "rejected" && (
                <button data-testid={`reject-${d.id}`} onClick={() => setApproval(d.id, "rejected")}
                  className="text-red-400 hover:text-red-300 transition-colors" title="Reject">
                  <XCircle size={18} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {section === "tiers" && (
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.email} data-testid={`tier-row-${u.email}`} className="flex items-center justify-between p-3 border border-white/10 bg-white/5 text-xs font-body">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Users size={14} className="text-white/40" />
                <div className="min-w-0">
                  <p className="text-white truncate">{u.name || u.email}</p>
                  <p className="text-white/40 truncate">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={u.tier || "basic"} onValueChange={(t) => setUserTier(u.email, t)}>
                  <SelectTrigger data-testid={`tier-select-${u.email}`} className="bg-white/5 border-white/15 text-white text-xs h-8 w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-luxury-black border-white/15">
                    {TIER_OPTIONS.map((t) => (
                      <SelectItem key={t.value} value={t.value} className="text-white">
                        <span className="inline-flex items-center gap-2"><t.icon size={12} /> {t.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}

      {section === "rides" && (
        <div className="space-y-2">
          {rides.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-md p-12 text-center text-white/40 font-body text-sm">No rides yet</div>
          ) : rides.slice(0, 30).map((r) => (
            <div key={r.id} data-testid={`ride-row-${r.id}`} className="p-3 border border-white/10 bg-white/5 rounded-md text-xs font-body">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-gold uppercase tracking-wider">{r.type === "food" ? "Food" : "Taxi"} · {r.id?.slice(0, 8)}</span>
                <span className={`px-2 py-0.5 ${
                  r.status === "completed" ? "text-green-400 bg-green-400/10" :
                  r.status === "cancelled" ? "text-red-400 bg-red-400/10" :
                  r.sos_triggered ? "text-red-400 bg-red-400/10" : "text-white/60 bg-white/10"
                }`}>{r.sos_triggered ? "SOS · " : ""}{r.status}</span>
              </div>
              <p className="text-white/80">{r.client_name} · JMD {r.estimated_fare} · {r.distance_km} km</p>
              <p className="text-white/50">{r.pickup?.address} → {r.dropoff?.address}</p>
              {r.driver_name && <p className="text-white/40">Driver: {r.driver_name} · {r.driver_plate}</p>}
            </div>
          ))}
        </div>
      )}

      {loading && <p className="text-gold/40 text-xs font-body">Refreshing…</p>}
    </div>
  );
}
